import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Wallet, ExternalLink, Loader2, Send, CheckCircle2, RefreshCw } from "lucide-react";
import api, { getUser } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

/**
 * Deposit plan card — used by the Gestor before sending the proposal to the
 * client. Defines `caução total`, `arranque de recrutamento` and `data de
 * arranque do serviço`. Once set, the proposal email can be sent.
 *
 * Polls the lightweight `/api/leads/{id}/deposit` endpoint every 20s while
 * a plan exists and the deposit is not yet fully paid, so that DAF-side
 * payments show up on the Gestor's screen without a manual refresh. Also
 * refreshes when the tab regains visibility (Gestor switches back from the
 * DAF portal or an email).
 */
export default function DepositPlanCard({ lead, onChange }) {
  const user = getUser();
  // 06/08/2026 — Consultor também pode definir a caução. Após a RT o
  // Consultor é quem melhor conhece a carga do cliente e o valor a
  // recomendar; obrigá-lo a esperar pelo Gestor bloqueava o fluxo.
  const canEdit = user?.role === "admin" || user?.role === "gestor" || user?.role === "consultor";

  const deposit = lead?.deposit || {};
  const plan = deposit.plan || {};
  const hasPlan = Boolean(plan.total_amount && plan.kickstart_amount);
  const dafToken = deposit.daf_token;
  const dafPortal = dafToken ? `/daf/${dafToken}` : null;
  const notifiedAt = deposit.daf_notified_at;

  const kickstartPaidAmount = deposit.kickstart_paid_amount || 0;
  const totalPaidAmount = deposit.total_paid_amount || 0;
  const kickstartPaid = Boolean(deposit.kickstart_paid);
  const fullyPaid = Boolean(deposit.fully_paid);

  const [dlgOpen, setDlgOpen] = useState(false);
  const [total, setTotal] = useState(plan.total_amount || "");
  const [kick, setKick] = useState(plan.kickstart_amount || "");
  const [startDate, setStartDate] = useState(plan.service_start_date || "");
  const [saving, setSaving] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [manualRefreshing, setManualRefreshing] = useState(false);

  // Poll the deposit state so DAF confirmations show up without a full reload.
  const lastSigRef = useRef(null);
  useEffect(() => {
    if (!lead?.id || !hasPlan || fullyPaid) return undefined;
    let cancelled = false;
    const sigOf = (d) => `${d?.total_paid_amount || 0}|${d?.kickstart_paid_amount || 0}|${(d?.payments || []).length}|${d?.daf_notified_at || ""}|${d?.invoice?.uploaded_at || ""}`;
    lastSigRef.current = sigOf(deposit);
    const tick = async () => {
      try {
        const r = await api.get(`/leads/${lead.id}/deposit`);
        if (cancelled) return;
        setLastSyncedAt(new Date());
        const sig = sigOf(r.data.deposit);
        if (sig !== lastSigRef.current) {
          lastSigRef.current = sig;
          onChange && onChange();
        }
      } catch (e) { /* silent */ }
    };
    const id = setInterval(tick, 20000);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { cancelled = true; clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id, hasPlan, fullyPaid]);

  const manualRefresh = async () => {
    setManualRefreshing(true);
    try {
      onChange && (await onChange());
      setLastSyncedAt(new Date());
      toast.success("Estado da caução atualizado");
    } finally { setManualRefreshing(false); }
  };

  const openDlg = () => {
    setTotal(plan.total_amount || "");
    setKick(plan.kickstart_amount || "");
    setStartDate(plan.service_start_date || "");
    setDlgOpen(true);
  };

  const save = async () => {
    const t = parseFloat(total), k = parseFloat(kick);
    if (!t || t <= 0) { toast.error("Valor total inválido"); return; }
    if (!k || k <= 0) { toast.error("Valor de arranque inválido"); return; }
    if (k > t) { toast.error("Arranque não pode exceder o total"); return; }
    if (!startDate) { toast.error("Indique a data de arranque"); return; }
    setSaving(true);
    try {
      await api.patch(`/leads/${lead.id}/deposit/plan`, {
        total_amount: t, kickstart_amount: k, service_start_date: startDate,
      });
      toast.success("Plano de caução guardado.");
      setDlgOpen(false);
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally { setSaving(false); }
  };

  const notifyDaf = async () => {
    setNotifying(true);
    try {
      const r = await api.post(`/leads/${lead.id}/deposit/notify-daf`);
      toast.success(`Email enviado a ${r.data.daf_email}`);
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao notificar DAF");
    } finally { setNotifying(false); }
  };

  // 06/08/2026 — O card passa a aparecer também depois da RT
  // realizada, mesmo antes de existir uma proposta. O Consultor
  // precisa de definir/rever a caução assim que sai da RT — antes
  // era só mostrado ao Gestor, o que bloqueava o fluxo.
  const rtDone = ["qt_realizada", "proposta_enviada", "aceite"].includes(lead?.status);
  if (!lead?.proposal && !rtDone) return null;

  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:p-8" data-testid="deposit-plan-card">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">
            Caução
          </div>
          <h3 className="font-ui text-lg inline-flex items-center gap-2">
            <Wallet className="w-5 h-5 text-foreground" />
            Plano de caução e faturação DAF
          </h3>
        </div>
        {hasPlan && (
          <div className="inline-flex items-center gap-2 flex-wrap">
            {!fullyPaid && (
              <button
                onClick={manualRefresh}
                disabled={manualRefreshing}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] text-muted-foreground hover:text-foreground disabled:opacity-50"
                data-testid="deposit-refresh-btn"
                title="Verificar pagamentos DAF agora"
              >
                <RefreshCw className={`w-3 h-3 ${manualRefreshing ? "animate-spin" : ""}`} />
                {lastSyncedAt
                  ? `Atualizado às ${lastSyncedAt.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}`
                  : "Verificar agora"}
              </button>
            )}
            <span
              className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-semibold px-3 py-1.5 rounded-full border ${
                fullyPaid ? "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/40 text-[hsl(var(--success))]"
                          : kickstartPaid ? "bg-primary/20 border-primary/40 text-foreground"
                                            : "bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))]"
              }`}
              data-testid="deposit-plan-status"
            >
              {fullyPaid ? "Totalmente paga" : kickstartPaid ? "Arranque pago" : "Aguarda pagamento"}
            </span>
          </div>
        )}
      </div>

      {!hasPlan ? (
        <div className="rounded-lg border border-[hsl(var(--warning))]/25 bg-[hsl(var(--warning-bg))] p-4 flex items-center justify-between flex-wrap gap-3" data-testid="deposit-plan-empty">
          <div className="text-sm text-[hsl(var(--warning))]">
            <b>Ainda não está definida.</b> Defina os valores da caução para poder enviar a proposta ao cliente.
          </div>
          {canEdit && (
            <button
              onClick={openDlg}
              className="inline-flex items-center gap-1.5 text-xs bg-amber-600 hover:bg-amber-700 text-primary-foreground rounded-full px-4 py-2 font-semibold"
              data-testid="deposit-plan-define-btn"
            >
              <Wallet className="w-3.5 h-3.5" /> Definir caução
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {/* Plan values */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-muted/40 border border-border rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Caução total</div>
              <div className="font-ui text-lg text-foreground mt-1" data-testid="plan-total">
                {plan.total_amount?.toFixed(2)}€
              </div>
            </div>
            <div className="bg-muted/40 border border-border rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Arranque recrutamento</div>
              <div className="font-ui text-lg text-foreground mt-1" data-testid="plan-kickstart">
                {plan.kickstart_amount?.toFixed(2)}€
              </div>
            </div>
            <div className="bg-muted/40 border border-border rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Arranque do serviço</div>
              <div className="font-ui text-lg text-foreground mt-1" data-testid="plan-start-date">
                {plan.service_start_date || "—"}
              </div>
            </div>
          </div>

          {/* Progress */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className={`rounded-lg p-3 border ${kickstartPaid ? "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25" : "bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/25"}`} data-testid="progress-kickstart">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground">
                {kickstartPaid ? <CheckCircle2 className="w-3 h-3 text-[hsl(var(--success))]" /> : null}
                Arranque
              </div>
              <div className="text-sm mt-1 font-ui">
                {kickstartPaidAmount.toFixed(2)}€ / {plan.kickstart_amount?.toFixed(2)}€
              </div>
            </div>
            <div className={`rounded-lg p-3 border ${fullyPaid ? "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25" : "bg-muted border-border"}`} data-testid="progress-total">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground">
                {fullyPaid ? <CheckCircle2 className="w-3 h-3 text-[hsl(var(--success))]" /> : null}
                Caução total recebida
              </div>
              <div className="text-sm mt-1 font-ui">
                {totalPaidAmount.toFixed(2)}€ / {plan.total_amount?.toFixed(2)}€
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            {canEdit && (
              <button onClick={openDlg} className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5" data-testid="deposit-plan-edit">
                <Wallet className="w-3.5 h-3.5" /> Editar plano
              </button>
            )}
            {dafPortal && (
              <a
                href={dafPortal} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5"
                data-testid="daf-portal-link"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Abrir portal DAF
              </a>
            )}
            {canEdit && (lead?.contract_data?.completed) && (
              <button onClick={notifyDaf} disabled={notifying}
                className="inline-flex items-center gap-1.5 text-xs bg-foreground hover:bg-slate-800 text-background rounded-full px-3 py-1.5 font-semibold disabled:opacity-50"
                data-testid="notify-daf-btn"
              >
                {notifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Notificar DAF{notifiedAt ? " novamente" : ""}
              </button>
            )}
            {notifiedAt && (
              <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground" data-testid="daf-notified-at">
                Notificado em {new Date(notifiedAt).toLocaleString("pt-PT")}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-lg bg-card" data-testid="deposit-plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl inline-flex items-center gap-2">
              <Wallet className="w-5 h-5 text-foreground" /> Definir caução
            </DialogTitle>
            <DialogDescription>
              O valor de arranque é parte do total. Depois de definido, a proposta pode ser enviada e o portal do DAF fica disponível.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1">Caução total (€)</div>
              <input type="number" step="0.01" value={total} onChange={(e) => setTotal(e.target.value)}
                     placeholder="Ex: 1200"
                     className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm"
                     data-testid="plan-input-total" />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1">Arranque de recrutamento (€)</div>
              <input type="number" step="0.01" value={kick} onChange={(e) => setKick(e.target.value)}
                     placeholder="Ex: 400"
                     className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm"
                     data-testid="plan-input-kickstart" />
              <div className="text-[10px] text-muted-foreground mt-1">Deve ser ≤ caução total. Este valor desbloqueia o recrutamento.</div>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1">Data de arranque do serviço</div>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                     className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm"
                     data-testid="plan-input-start-date" />
              <div className="text-[10px] text-muted-foreground mt-1">Alertas 48h antes desta data se a caução total não estiver confirmada.</div>
            </label>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setDlgOpen(false)} className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:bg-muted" data-testid="plan-cancel">Cancelar</button>
            <button onClick={save} disabled={saving} className="px-5 py-2 rounded-lg bg-foreground hover:bg-slate-800 text-background text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid="plan-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
              Guardar plano
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
